// projects.ts

export type ProjectCategory =
    | 'All'
    | 'AI & Automation'
    | 'Web Applications'
    | 'Mobile Apps'
    | 'Websites';

export interface ProjectStat {
    value: string;
    label: string;
    description: string;
}

export interface ProjectTestimonial {
    quote: string;
    name: string;
    role: string;
    avatar: string;
}

/**
 * Keep existing fields exactly the same, but add nested translations for details.
 * This allows your UI to keep using the same keys, now per locale.
 */
export interface ProjectDetailsContent {
    nl: {
        breadcrumbLabel?: string;
        breadcrumbCategory?: string;

        client?: string;
        industry?: string;
        duration?: string;
        service?: string;

        challengeTitle?: string;
        challengeIcon?: string;
        challengeParagraphs?: string[];

        solutionTitle?: string;
        solutionIcon?: string;
        solutionParagraphs?: string[];

        technologiesTitle?: string;
        technologies?: { icon: string; label: string }[];

        resultsTitle?: string;
        resultsSubtitle?: string;
        stats?: ProjectStat[];
        testimonial?: ProjectTestimonial;

        ctaTitle?: string;
        ctaSubtitle?: string;
    };

    en: {
        breadcrumbLabel?: string;
        breadcrumbCategory?: string;

        client?: string;
        industry?: string;
        duration?: string;
        service?: string;

        challengeTitle?: string;
        challengeIcon?: string;
        challengeParagraphs?: string[];

        solutionTitle?: string;
        solutionIcon?: string;
        solutionParagraphs?: string[];

        technologiesTitle?: string;
        technologies?: { icon: string; label: string }[];

        resultsTitle?: string;
        resultsSubtitle?: string;
        stats?: ProjectStat[];
        testimonial?: ProjectTestimonial;

        ctaTitle?: string;
        ctaSubtitle?: string;
    };
}

export type ProjectLocale = 'nl' | 'en';

export interface ProjectTranslation {
    title: string;
    category: Exclude<ProjectCategory, 'All'>; // keep categories consistent (filter keys)
    tag: string;
    summary: string;
    /**
     * One-line outcome shown under the title on the homepage cards. Keep it to a
     * single measurable result; the card hides the line when it is absent.
     */
    result?: string;
    details?: ProjectDetailsContent;
}

export interface ProjectItem {
    slug: string;
    image: string;
    nl: ProjectTranslation;
    en: ProjectTranslation;
}

export const projectCategories: ProjectCategory[] = [
    'All',
    'AI & Automation',
    'Web Applications',
    'Mobile Apps',
    'Websites',
];

const defaultDetails: ProjectDetailsContent = {
    en: {
        breadcrumbLabel: 'Work',
        breadcrumbCategory: 'Case Study',
        client: 'BitPeak Client',
        industry: 'Digital',
        duration: 'Ongoing',
        service: 'Web App Dev',
        challengeTitle: 'The Challenge',
        challengeIcon: 'warning',
        challengeParagraphs: [
            'This is a placeholder challenge section. Replace this text with your real project details.',
        ],
        solutionTitle: 'The Solution',
        solutionIcon: 'lightbulb',
        solutionParagraphs: [
            'This is a placeholder solution section. Replace this text with your real project details.',
        ],
        technologiesTitle: 'Used Technologies',
        technologies: [
            { icon: 'code', label: 'Vue' },
            { icon: 'dataset', label: 'Laravel' },
            { icon: 'cloud', label: 'Cloud' },
            { icon: 'terminal', label: 'Node.js' },
            { icon: 'smartphone', label: 'Flutter' },
        ],
        resultsTitle: 'The Results',
        resultsSubtitle: 'Delivering measurable impact.',
        stats: [
            {
                value: '+40%',
                label: 'Conversion Rate',
                description: 'Optimizations reduced drop-off significantly.',
            },
            {
                value: '0.8s',
                label: 'Page Load Time',
                description: 'Performance improvements via caching and optimizations.',
            },
            {
                value: '100%',
                label: 'Uptime',
                description: 'Stable platform during peak traffic.',
            },
        ],
        testimonial: {
            quote:
                "BitPeak didn't just write code; they understood our business bottleneck. The new platform paid for itself in the first month of sales.",
            name: 'Sarah Jenkins',
            role: 'CTO, RetailGiant',
            avatar:
                'https://lh3.googleusercontent.com/aida-public/AB6AXuB4alRR_zK330tRNQ_nv92Imazxmlrw9_-Mby5zpuf6qfHUk3R7ZXugqywVo2xyWHN6V4wWVnQKgv7oC7zY2I2Zr7tJQxCBci0LvYFrMPwCZIQuWk1tgRkeqKvVucvhXMUr34SQmJ4DL1yHvLHig0GJZIVHjPBds5PkT7WV4o8gtCVbHx3Tql0Kw0gh7eL7dAa1hz1456JDACL_9ZRk_9orc-5f6KV119imGsd5Bh0mjPSh-0FNSXwZYVsfVTTR4Np3sNRF_CfqvXA',
        },
        ctaTitle: 'Ready to scale your business?',
        ctaSubtitle:
            'We bring affordable quality and clear communication to every project. No fine print, just results.',
    },

    nl: {
        breadcrumbLabel: 'Werk',
        breadcrumbCategory: 'Case Study',
        client: 'BitPeak klant',
        industry: 'Digitaal',
        duration: 'Doorlopend',
        service: 'Webapplicatieontwikkeling',
        challengeTitle: 'De uitdaging',
        challengeIcon: 'warning',
        challengeParagraphs: [
            'Dit is een placeholder voor de uitdaging. Vervang deze tekst met je echte projectdetails.',
        ],
        solutionTitle: 'De oplossing',
        solutionIcon: 'lightbulb',
        solutionParagraphs: [
            'Dit is een placeholder voor de oplossing. Vervang deze tekst met je echte projectdetails.',
        ],
        technologiesTitle: 'Gebruikte technologieën',
        technologies: [
            { icon: 'code', label: 'Vue' },
            { icon: 'dataset', label: 'Laravel' },
            { icon: 'cloud', label: 'Cloud' },
            { icon: 'terminal', label: 'Node.js' },
            { icon: 'smartphone', label: 'Flutter' },
        ],
        resultsTitle: 'Het resultaat',
        resultsSubtitle: 'Meetbare impact.',
        stats: [
            {
                value: '+40%',
                label: 'Conversie',
                description: 'Optimalisaties verlaagden de uitval aanzienlijk.',
            },
            {
                value: '0.8s',
                label: 'Laadtijd',
                description: 'Performance verbeterd met caching en optimalisaties.',
            },
            {
                value: '100%',
                label: 'Uptime',
                description: 'Stabiel platform tijdens piekdrukte.',
            },
        ],
        testimonial: {
            quote:
                'BitPeak leverde niet alleen code, maar begreep ook onze knelpunten. Het platform verdiende zichzelf snel terug.',
            name: 'Sarah Jenkins',
            role: 'CTO, RetailGiant',
            avatar:
                'https://lh3.googleusercontent.com/aida-public/AB6AXuB4alRR_zK330tRNQ_nv92Imazxmlrw9_-Mby5zpuf6qfHUk3R7ZXugqywVo2xyWHN6V4wWVnQKgv7oC7zY2I2Zr7tJQxCBci0LvYFrMPwCZIQuWk1tgRkeqKvVucvhXMUr34SQmJ4DL1yHvLHig0GJZIVHjPBds5PkT7WV4o8gtCVbHx3Tql0Kw0gh7eL7dAa1hz1456JDACL_9ZRk_9orc-5f6KV119imGsd5Bh0mjPSh-0FNSXwZYVsfVTTR4Np3sNRF_CfqvXA',
        },
        ctaTitle: 'Klaar om op te schalen?',
        ctaSubtitle:
            'Betaalbare kwaliteit en duidelijke communicatie bij elk project. Geen kleine lettertjes, wel resultaat.',
    },
};

export const projects: ProjectItem[] = [
    {
        slug: 'sman-auto-ai',
        image: '/images/projects/smancars.webp',
        en: {
            title: 'Sman Auto AI',
            category: 'AI & Automation',
            tag: 'Automotive AI',
            summary:
                'AI that turns raw dealer photos into consistent, webshop ready vehicle imagery, straight from the Sman Cars admin.',
            details: {
                ...defaultDetails,
                en: {
                    breadcrumbLabel: 'Projects',
                    breadcrumbCategory: 'AI & Automation',
                    client: 'Sman Cars',
                    industry: 'Automotive',
                    duration: 'Ongoing',
                    service: 'AI Development',
                    challengeTitle: 'The challenge',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Vehicle photos are shot on the lot in whatever light and surroundings the day offers, so no two listings look alike.',
                        'Editing every set of photos by hand costs hours per car and still leaves the webshop looking inconsistent.',
                        'Weak imagery directly hurts trust and conversion on a page where the photo is the product.',
                    ],
                    solutionTitle: 'The solution',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We built an AI layer into the Sman Cars platform that generates and enhances webshop ready vehicle images.',
                        'Sales staff upload the photos they already take, and the AI returns a clean, consistent set with the same framing and presentation across every listing.',
                        'The processing runs on queues in the background, so publishing a car never waits on image work.',
                    ],
                    technologiesTitle: 'Used technologies',
                    technologies: [
                        { icon: 'auto_awesome', label: 'AI Image Generation' },
                        { icon: 'dataset', label: 'Laravel' },
                        { icon: 'code', label: 'Vue.js' },
                        { icon: 'schedule', label: 'Queues & Jobs' },
                        { icon: 'image', label: 'Image Processing' },
                    ],
                    resultsTitle: 'The results',
                    resultsSubtitle: 'A webshop where every car is presented at the same quality.',
                    stats: [
                        {
                            value: 'Consistent',
                            label: 'Listings',
                            description: 'One visual standard across the entire inventory.',
                        },
                        {
                            value: 'Minutes',
                            label: 'Per vehicle',
                            description: 'Photo work that used to be manual now runs automatically.',
                        },
                        {
                            value: 'In-platform',
                            label: 'Workflow',
                            description: 'No external editing tools, everything happens in the admin.',
                        },
                    ],
                    ctaTitle: 'Want AI inside your own platform?',
                    ctaSubtitle:
                        'We build AI features that fit an existing product instead of sitting next to it.',
                },

                nl: {
                    ...defaultDetails.nl,
                },
            },
        },

        nl: {
            title: 'Sman Auto AI',
            category: 'AI & Automation',
            tag: 'Automotive AI',
            summary:
                'AI die ruwe dealerfoto’s omzet naar consistente, webshopwaardige voertuigbeelden, direct vanuit de Sman Cars admin.',
            details: {
                ...defaultDetails,
                nl: {
                    breadcrumbLabel: 'Werk',
                    breadcrumbCategory: 'AI & automatisering',
                    client: 'Sman Cars',
                    industry: 'Automotive',
                    duration: 'Doorlopend',
                    service: 'AI-ontwikkeling',
                    challengeTitle: 'De uitdaging',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Voertuigfoto’s worden op het terrein geschoten, in het licht en de omgeving van dat moment. Daardoor lijkt geen enkele listing op de andere.',
                        'Elke set foto’s met de hand bewerken kost uren per auto en levert alsnog een rommelige webshop op.',
                        'Zwakke beelden raken direct het vertrouwen en de conversie, juist op een pagina waar de foto het product is.',
                    ],
                    solutionTitle: 'De oplossing',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We bouwden een AI-laag in het Sman Cars platform die webshopwaardige voertuigbeelden genereert en verbetert.',
                        'Verkopers uploaden de foto’s die ze toch al maken en de AI levert een strakke, consistente set terug met dezelfde uitsnede en presentatie voor elke listing.',
                        'De verwerking draait op de achtergrond via queues, zodat het publiceren van een auto nooit wacht op beeldwerk.',
                    ],
                    technologiesTitle: 'Gebruikte technologieën',
                    technologies: [
                        { icon: 'auto_awesome', label: 'AI-beeldgeneratie' },
                        { icon: 'dataset', label: 'Laravel' },
                        { icon: 'code', label: 'Vue.js' },
                        { icon: 'schedule', label: 'Queues & jobs' },
                        { icon: 'image', label: 'Beeldverwerking' },
                    ],
                    resultsTitle: 'Het resultaat',
                    resultsSubtitle: 'Een webshop waarin elke auto op hetzelfde niveau staat.',
                    stats: [
                        {
                            value: 'Consistent',
                            label: 'Listings',
                            description: 'Eén visuele standaard over de hele voorraad.',
                        },
                        {
                            value: 'Minuten',
                            label: 'Per voertuig',
                            description: 'Fotowerk dat eerst handmatig was, gaat nu automatisch.',
                        },
                        {
                            value: 'In het platform',
                            label: 'Workflow',
                            description: 'Geen externe bewerkingstools, alles gebeurt in de admin.',
                        },
                    ],
                    ctaTitle: 'Ook AI in je eigen platform?',
                    ctaSubtitle:
                        'Wij bouwen AI-functionaliteit die in een bestaand product past in plaats van ernaast staat.',
                },
            },
        },
    },
    {
        slug: 'suporta-agents',
        image: '/images/projects/suporta.webp',
        en: {
            title: 'Suporta Agents',
            category: 'AI & Automation',
            tag: 'AI Agents',
            summary:
                'Six AI agents inside Suporta that take a ticket from plan to pull request: planning, coding across repositories, checks, review, and documentation.',
            details: {
                ...defaultDetails,
                en: {
                    breadcrumbLabel: 'Projects',
                    breadcrumbCategory: 'AI & Automation',
                    client: 'BitPeak (Internal)',
                    industry: 'Productivity & Operations',
                    duration: 'Ongoing',
                    service: 'AI Development',
                    challengeTitle: 'The challenge',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'A support ticket and the code that resolves it live in two different worlds. Someone reads the ticket, opens the repository, finds the right spot, and writes the change.',
                        'A large part of that work is mechanical: set up a branch, gather context, run the tests, write the pull request description.',
                        'On top of that, a single feature often spans more than one repository, an API and an admin app for example, so the same context has to be rebuilt twice.',
                    ],
                    solutionTitle: 'The solution',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We built an agent pipeline into Suporta, our own helpdesk and project platform. Straight from a ticket, a run prepares an isolated workspace per linked repository: git mirror, worktree, and branch.',
                        'A planner agent first investigates the code read only and writes an implementation plan. The coding agent follows that plan and works across every linked repository at once, so one coherent solution can cover an API and its admin app.',
                        'After the changes the repository runs its own lint and test commands, the branch is pushed, and a pull request is opened per changed repository. Review and documentation agents check the result, and feedback from a developer starts another round on the same branch.',
                        'The agents never merge. Every run ends in a pull request that a human approves, with each step visible in a live activity feed. Teams bring their own model and can run the agents locally through an MCP server, and the token usage per run is tracked so it can be billed to the client.',
                    ],
                    technologiesTitle: 'Used technologies',
                    technologies: [
                        { icon: 'smart_toy', label: 'AI Agents' },
                        { icon: 'dataset', label: 'Laravel' },
                        { icon: 'code', label: 'Vue.js' },
                        { icon: 'merge', label: 'Git & Pull Requests' },
                        { icon: 'hub', label: 'MCP Server' },
                    ],
                    resultsTitle: 'The results',
                    resultsSubtitle: 'A helpdesk that also ships the work.',
                    stats: [
                        {
                            value: 'Plan to PR',
                            label: 'Pipeline',
                            description: 'One run goes from ticket to reviewed pull request.',
                        },
                        {
                            value: 'Multi-repo',
                            label: 'Scope',
                            description: 'API and admin app changed in one coherent solution.',
                        },
                        {
                            value: 'Never merges',
                            label: 'Control',
                            description: 'Every change waits for human approval in a pull request.',
                        },
                    ],
                    ctaTitle: 'Curious what agents could take off your plate?',
                    ctaSubtitle:
                        'We build AI agents around a real workflow, starting with the step that costs your team the most time.',
                },

                nl: {
                    ...defaultDetails.nl,
                },
            },
        },

        nl: {
            title: 'Suporta Agents',
            category: 'AI & Automation',
            tag: 'AI-agents',
            summary:
                'Zes AI-agents in Suporta die een ticket van plan naar pull request brengen: plannen, coderen over meerdere repositories, checks, review en documentatie.',
            details: {
                ...defaultDetails,
                nl: {
                    breadcrumbLabel: 'Werk',
                    breadcrumbCategory: 'AI & automatisering',
                    client: 'BitPeak (intern)',
                    industry: 'Productiviteit & operations',
                    duration: 'Doorlopend',
                    service: 'AI-ontwikkeling',
                    challengeTitle: 'De uitdaging',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Een supportticket en de code die het oplost leven in twee verschillende werelden. Iemand leest het ticket, opent de repository, zoekt de juiste plek en schrijft de wijziging.',
                        'Een groot deel van dat werk is mechanisch: een branch opzetten, context verzamelen, tests draaien, de pull request-omschrijving schrijven.',
                        'Daar komt bij dat één feature vaak meerdere repositories raakt, bijvoorbeeld een API en een adminapp, waardoor dezelfde context twee keer opgebouwd moet worden.',
                    ],
                    solutionTitle: 'De oplossing',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We bouwden een agent-pipeline in Suporta, ons eigen helpdesk- en projectplatform. Direct vanuit een ticket zet een run per gekoppelde repository een geïsoleerde werkomgeving klaar: git-mirror, worktree en branch.',
                        'Een planner-agent onderzoekt eerst read-only de code en schrijft een implementatieplan. De coding-agent volgt dat plan en werkt in alle gekoppelde repositories tegelijk, zodat één samenhangende oplossing zowel de API als de adminapp kan omvatten.',
                        'Na de wijzigingen draait de repository zijn eigen lint- en testcommando’s, wordt de branch gepusht en gaat er per gewijzigde repository een pull request open. Review- en documentatie-agents controleren het resultaat en feedback van een developer start een nieuwe ronde op dezelfde branch.',
                        'De agents mergen nooit. Elke run eindigt in een pull request die een mens goedkeurt, met elke stap zichtbaar in een live activiteitenfeed. Teams gebruiken hun eigen model en kunnen de agents lokaal draaien via een MCP-server, en het tokenverbruik per run wordt bijgehouden zodat het aan de klant doorbelast kan worden.',
                    ],
                    technologiesTitle: 'Gebruikte technologieën',
                    technologies: [
                        { icon: 'smart_toy', label: 'AI-agents' },
                        { icon: 'dataset', label: 'Laravel' },
                        { icon: 'code', label: 'Vue.js' },
                        { icon: 'merge', label: 'Git & pull requests' },
                        { icon: 'hub', label: 'MCP-server' },
                    ],
                    resultsTitle: 'Het resultaat',
                    resultsSubtitle: 'Een helpdesk die het werk ook uitvoert.',
                    stats: [
                        {
                            value: 'Plan naar PR',
                            label: 'Pipeline',
                            description: 'Eén run gaat van ticket naar beoordeelde pull request.',
                        },
                        {
                            value: 'Multi-repo',
                            label: 'Bereik',
                            description: 'API en adminapp aangepast in één samenhangende oplossing.',
                        },
                        {
                            value: 'Mergt nooit',
                            label: 'Controle',
                            description: 'Elke wijziging wacht op goedkeuring van een mens in een pull request.',
                        },
                    ],
                    ctaTitle: 'Benieuwd wat agents jou uit handen nemen?',
                    ctaSubtitle:
                        'Wij bouwen AI-agents rondom een echte workflow, te beginnen bij de stap die je team de meeste tijd kost.',
                },
            },
        },
    },
    {
        slug: 'bitpeak-proposal-engine',
        image: '/images/hero-collage-1200.webp',
        en: {
            title: 'BitPeak Proposal Engine',
            category: 'AI & Automation',
            tag: 'AI Automation',
            summary:
                'Give it a company URL and it crawls the site, picks up the brand, and has Claude write a complete redesign of their homepage.',
            details: {
                ...defaultDetails,
                en: {
                    breadcrumbLabel: 'Projects',
                    breadcrumbCategory: 'AI & Automation',
                    client: 'BitPeak (Internal)',
                    industry: 'Sales & Marketing',
                    duration: 'Ongoing',
                    service: 'AI Development',
                    challengeTitle: 'The challenge',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Showing a prospect what their website could look like is the strongest pitch there is, and also the most expensive one to make.',
                        'Building a mockup by hand takes days, so it only ever happens after a company has already said yes to a meeting.',
                        'We wanted to flip that around and walk into the first conversation with the redesign already on the table.',
                    ],
                    solutionTitle: 'The solution',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'The engine crawls the prospect’s website, extracts their brand (logo, colors, typography) and classifies the market they operate in.',
                        'Claude then writes a complete one page redesign as a standalone document, using the company’s own brand and content instead of a generic template.',
                        'The whole pipeline runs asynchronously on queues, and the result is served as a shareable page with a booking flow attached.',
                    ],
                    technologiesTitle: 'Used technologies',
                    technologies: [
                        { icon: 'smart_toy', label: 'Claude (Anthropic)' },
                        { icon: 'dataset', label: 'Laravel' },
                        { icon: 'travel_explore', label: 'Crawler' },
                        { icon: 'photo_camera', label: 'Headless Chrome' },
                        { icon: 'schedule', label: 'Queues & Jobs' },
                    ],
                    resultsTitle: 'The results',
                    resultsSubtitle: 'A personalized redesign before the first meeting, not after it.',
                    stats: [
                        {
                            value: 'From a URL',
                            label: 'Input',
                            description: 'One company link is enough to start the pipeline.',
                        },
                        {
                            value: 'On brand',
                            label: 'Output',
                            description: 'The prospect’s own logo, colors, and content, not a template.',
                        },
                        {
                            value: 'Automated',
                            label: 'Pipeline',
                            description: 'Crawl, brand extraction, generation, and screenshots run unattended.',
                        },
                    ],
                    ctaTitle: 'Want to see what AI can automate in your sales process?',
                    ctaSubtitle:
                        'We built this for ourselves first. Happy to show you how it works.',
                },

                nl: {
                    ...defaultDetails.nl,
                },
            },
        },

        nl: {
            title: 'BitPeak Proposal Engine',
            category: 'AI & Automation',
            tag: 'AI-automatisering',
            summary:
                'Geef een bedrijfs-URL en de engine crawlt de site, pakt de huisstijl op en laat Claude een complete redesign van hun homepage schrijven.',
            details: {
                ...defaultDetails,
                nl: {
                    breadcrumbLabel: 'Werk',
                    breadcrumbCategory: 'AI & automatisering',
                    client: 'BitPeak (intern)',
                    industry: 'Sales & marketing',
                    duration: 'Doorlopend',
                    service: 'AI-ontwikkeling',
                    challengeTitle: 'De uitdaging',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Een prospect laten zien hoe zijn website eruit kán zien is de sterkste pitch die er is, en meteen de duurste om te maken.',
                        'Een mockup met de hand bouwen kost dagen, dus gebeurde dat pas nadat een bedrijf al ja had gezegd tegen een gesprek.',
                        'Wij wilden dat omdraaien en het eerste gesprek ingaan met de redesign al op tafel.',
                    ],
                    solutionTitle: 'De oplossing',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'De engine crawlt de website van de prospect, haalt de huisstijl eruit (logo, kleuren, typografie) en bepaalt in welke markt het bedrijf zit.',
                        'Claude schrijft vervolgens een complete redesign van één pagina als losstaand document, met de eigen huisstijl en content van het bedrijf in plaats van een generieke template.',
                        'De hele pipeline draait asynchroon op queues en het resultaat wordt gepresenteerd als deelbare pagina met een boekingsflow eraan vast.',
                    ],
                    technologiesTitle: 'Gebruikte technologieën',
                    technologies: [
                        { icon: 'smart_toy', label: 'Claude (Anthropic)' },
                        { icon: 'dataset', label: 'Laravel' },
                        { icon: 'travel_explore', label: 'Crawler' },
                        { icon: 'photo_camera', label: 'Headless Chrome' },
                        { icon: 'schedule', label: 'Queues & jobs' },
                    ],
                    resultsTitle: 'Het resultaat',
                    resultsSubtitle: 'Een persoonlijke redesign vóór het eerste gesprek, niet erna.',
                    stats: [
                        {
                            value: 'Vanaf een URL',
                            label: 'Input',
                            description: 'Eén bedrijfslink is genoeg om de pipeline te starten.',
                        },
                        {
                            value: 'Eigen stijl',
                            label: 'Output',
                            description: 'Het logo, de kleuren en de content van de prospect zelf.',
                        },
                        {
                            value: 'Geautomatiseerd',
                            label: 'Pipeline',
                            description: 'Crawlen, huisstijl, generatie en screenshots draaien vanzelf.',
                        },
                    ],
                    ctaTitle: 'Benieuwd wat AI in jouw salesproces kan automatiseren?',
                    ctaSubtitle:
                        'We bouwden dit eerst voor onszelf. We laten graag zien hoe het werkt.',
                },
            },
        },
    },
    {
        slug: 'food-la-route-platform',
        image: '/images/projects/foodlaroute.webp',
        en: {
            title: 'Food la Route Platform',
            category: 'Web Applications',
            tag: 'Food Tours',
            summary:
                'A Benelux food tour platform with online registrations and a powerful admin suite for tour management, invoicing, smart group allocations, and email automation.',
            result: 'Fully automated from booking to invoicing.',
            details: {
                ...defaultDetails,
                en: {
                    breadcrumbLabel: 'Projects',
                    breadcrumbCategory: 'Web Application',
                    client: 'Food la Route',
                    industry: 'Events & Food Tours',
                    duration: 'Ongoing',
                    service: 'Web App Development',
                    challengeTitle: 'The challenge',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Food la Route runs food tours across the Benelux with large groups visiting multiple restaurants per event.',
                        'They needed a smooth online signup flow for customers and a staff-focused admin environment to manage participants, invoicing, and logistics at scale.',
                        'Operational complexity required smart group allocation and reliable communication with participants before and during tours.',
                    ],
                    solutionTitle: 'The solution',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We built a custom web platform combining a customer-facing registration flow with a robust admin dashboard for internal operations.',
                        'The admin enables tour creation and management, participant handling, automated invoicing, and smart group allocations to keep tours organized.',
                        'We added custom email automations and mailing in the admin for confirmations, updates, and follow-ups, ensuring consistent communication at scale.',
                    ],
                    technologiesTitle: 'Used technologies',
                    technologies: [
                        { icon: 'dataset', label: 'Laravel' },
                        { icon: 'code', label: 'Vue.js' },
                        { icon: 'database', label: 'MySQL' },
                        { icon: 'payments', label: 'Mollie' },
                        { icon: 'schedule', label: 'Queues & Jobs' },
                    ],
                    resultsTitle: 'The results',
                    resultsSubtitle:
                        'A scalable operational platform that keeps registrations, planning, and communication streamlined.',
                    stats: [
                        {
                            value: 'Benelux',
                            label: 'Coverage',
                            description: 'Supports food tours across multiple cities and regions.',
                        },
                        {
                            value: 'Admin-first',
                            label: 'Operations',
                            description: 'Centralized management for tours, participants, and invoicing.',
                        },
                        {
                            value: 'Automated',
                            label: 'Communication',
                            description: 'Custom email automations for confirmations, updates, and follow-ups.',
                        },
                    ],
                    ctaTitle: 'Planning a platform with operational complexity?',
                    ctaSubtitle:
                        'We build scalable web applications that keep operations simple, with clear communication, no fine print.',
                },
            },
        },

        nl: {
            title: 'Food la Route Platform',
            category: 'Web Applications',
            tag: 'Foodtours',
            summary:
                'Een Benelux foodtour-platform met online inschrijvingen en een krachtige adminomgeving voor tourbeheer, facturatie, slimme groepsindelingen en e-mailautomatisering.',
            result: 'Volledig geautomatiseerd van boeking tot facturatie.',
            details: {
                ...defaultDetails,
                nl: {
                    breadcrumbLabel: 'Werk',
                    breadcrumbCategory: 'Webapplicatie',
                    client: 'Food la Route',
                    industry: 'Events & Foodtours',
                    duration: 'Doorlopend',
                    service: 'Webapplicatieontwikkeling',
                    challengeTitle: 'De uitdaging',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Food la Route organiseert foodtours door de Benelux waarbij grote groepen meerdere restaurants bezoeken tijdens één event.',
                        'Er was behoefte aan een soepele inschrijf-flow voor klanten én een adminomgeving waarin medewerkers deelnemers, facturatie en logistiek op schaal kunnen beheren.',
                        'Door de operationele complexiteit waren slimme groepsindelingen en betrouwbare communicatie richting deelnemers essentieel.',
                    ],
                    solutionTitle: 'De oplossing',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We ontwikkelden een maatwerk webplatform met een klantgerichte registratieflow en een uitgebreide adminomgeving voor de interne operatie.',
                        'In de admin worden tours aangemaakt en beheerd, deelnemers gemanaged, facturen automatisch gegenereerd en groepsindelingen slim opgebouwd.',
                        'Daarnaast voegden we custom e-mailautomations en mailing toe in de admin voor bevestigingen, updates en opvolging, zodat communicatie consistent en schaalbaar blijft.',
                    ],
                    technologiesTitle: 'Gebruikte technologieën',
                    technologies: [
                        { icon: 'dataset', label: 'Laravel' },
                        { icon: 'code', label: 'Vue.js' },
                        { icon: 'database', label: 'MySQL' },
                        { icon: 'payments', label: 'Mollie' },
                        { icon: 'schedule', label: 'Queues & Jobs' },
                    ],
                    resultsTitle: 'Het resultaat',
                    resultsSubtitle:
                        'Een schaalbaar platform dat inschrijvingen, planning en communicatie gestroomlijnd houdt.',
                    stats: [
                        {
                            value: 'Benelux',
                            label: 'Dekking',
                            description: 'Ondersteunt foodtours in meerdere steden en regio’s.',
                        },
                        {
                            value: 'Admin-first',
                            label: 'Operatie',
                            description: 'Centrale aansturing van tours, deelnemers en facturatie.',
                        },
                        {
                            value: 'Geautomatiseerd',
                            label: 'Communicatie',
                            description: 'Custom e-mailflows voor bevestiging, updates en opvolging.',
                        },
                    ],
                    ctaTitle: 'Ook een platform met operationele complexiteit?',
                    ctaSubtitle:
                        'Wij bouwen schaalbare webapplicaties die je operatie simpel houden, met heldere communicatie, geen kleine lettertjes.',
                },
            },
        },
    },
    {
        slug: 'food-la-route-app',
        image: '/images/projects/foodlarouteapp.webp',
        en: {
            title: 'Food la Route App',
            category: 'Mobile Apps',
            tag: 'Food Tours',
            summary:
                'A participant-focused mobile app that improves the food tour experience with smart notifications, city tips, and in-app webshop access.',
            details: {
                ...defaultDetails,
                en: {
                    breadcrumbLabel: 'Projects',
                    breadcrumbCategory: 'Mobile App',
                    client: 'Food la Route',
                    industry: 'Events & Food Tours',
                    duration: 'Ongoing',
                    service: 'Mobile App Development',
                    challengeTitle: 'The challenge',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Food tours involve multiple stops, time slots, and participant groups, so the experience must stay clear, guided, and enjoyable throughout the day.',
                        'Participants needed real-time guidance on when to move to the next restaurant, plus helpful context about the city and the event.',
                        'Food la Route also wanted their webshop to be instantly accessible within the app to increase convenience and conversions.',
                    ],
                    solutionTitle: 'The solution',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We built a mobile app focused on participant experience and event guidance, connected to the Food la Route platform.',
                        'The app sends smart notifications, such as city tips for nearby places and reminders when it’s time to move to the next restaurant.',
                        'The webshop is directly available inside the app, removing friction and making it easy for users to purchase instantly.',
                    ],
                    technologiesTitle: 'Used technologies',
                    technologies: [
                        { icon: 'smartphone', label: 'Flutter' },
                        { icon: 'dataset', label: 'API Integration' },
                        { icon: 'notifications', label: 'Push Notifications' },
                        { icon: 'shopping_cart', label: 'In-app Webshop' },
                        { icon: 'shield', label: 'Secure Auth' },
                    ],
                    resultsTitle: 'The results',
                    resultsSubtitle:
                        'A smoother tour experience with better engagement and a more accessible webshop.',
                    stats: [
                        {
                            value: 'Real-time',
                            label: 'Guidance',
                            description: 'Notifications help participants move to the next stop at the right moment.',
                        },
                        {
                            value: 'Local',
                            label: 'City Tips',
                            description: 'Helpful suggestions for interesting places during the tour.',
                        },
                        {
                            value: 'In-app',
                            label: 'Webshop',
                            description: 'Direct access increases convenience and reduces drop-off.',
                        },
                    ],
                    ctaTitle: 'Want to improve your customer experience with an app?',
                    ctaSubtitle:
                        'We build mobile apps that guide users, drive engagement, and connect seamlessly to your platform.',
                },
                nl: {
                    // Fallback to EN if NL block isn’t explicitly set elsewhere
                    ...defaultDetails.nl,
                },
            },
        },

        nl: {
            title: 'Food la Route App',
            category: 'Mobile Apps',
            tag: 'Foodtours',
            summary:
                'Een mobiele app die de foodtour-ervaring verbetert met slimme notificaties, tips in de stad en directe toegang tot de webshop in de app.',
            details: {
                ...defaultDetails,
                nl: {
                    breadcrumbLabel: 'Werk',
                    breadcrumbCategory: 'Mobiele app',
                    client: 'Food la Route',
                    industry: 'Events & Foodtours',
                    duration: 'Doorlopend',
                    service: 'Mobiele app ontwikkeling',
                    challengeTitle: 'De uitdaging',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Foodtours bestaan uit meerdere stops, tijdsloten en groepen, waardoor de ervaring voor deelnemers de hele dag duidelijk en begeleid moet blijven.',
                        'Deelnemers hadden behoefte aan real-time begeleiding: wanneer ze door moeten naar het volgende restaurant, én handige context over de stad en het event.',
                        'Daarnaast wilde Food la Route de webshop direct in de app beschikbaar maken om drempels te verlagen en aankopen makkelijker te maken.',
                    ],
                    solutionTitle: 'De oplossing',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We ontwikkelden een mobiele app die volledig gericht is op de deelnemerservaring en tourbegeleiding, gekoppeld aan het Food la Route platform.',
                        'De app verstuurt slimme notificaties, zoals tips voor leuke plekken in de buurt en meldingen wanneer het tijd is om door te gaan naar het volgende restaurant.',
                        'De webshop is direct beschikbaar in de app, zodat gebruikers zonder gedoe kunnen bestellen.',
                    ],
                    technologiesTitle: 'Gebruikte technologieën',
                    technologies: [
                        { icon: 'smartphone', label: 'Flutter' },
                        { icon: 'dataset', label: 'API-integratie' },
                        { icon: 'notifications', label: 'Push-notificaties' },
                        { icon: 'shopping_cart', label: 'Webshop in-app' },
                        { icon: 'shield', label: 'Veilige authenticatie' },
                    ],
                    resultsTitle: 'Het resultaat',
                    resultsSubtitle:
                        'Een soepelere tourervaring met hogere betrokkenheid en een beter toegankelijke webshop.',
                    stats: [
                        {
                            value: 'Real-time',
                            label: 'Begeleiding',
                            description: 'Notificaties helpen deelnemers op het juiste moment door te gaan.',
                        },
                        {
                            value: 'Lokaal',
                            label: 'Stadstips',
                            description: 'Handige suggesties voor leuke plekken tijdens de tour.',
                        },
                        {
                            value: 'In-app',
                            label: 'Webshop',
                            description: 'Directe toegang verlaagt drempels en vermindert uitval.',
                        },
                    ],
                    ctaTitle: 'Ook een app die je klantreis verbetert?',
                    ctaSubtitle:
                        'Wij bouwen mobiele apps die gebruikers begeleiden, engagement verhogen en naadloos koppelen aan je platform.',
                },
                en: {
                    // Fallback to EN if needed
                    ...defaultDetails.en,
                },
            },
        },
    },
    {
        slug: 'sman-cars-platform',
        image: '/images/projects/smancars.webp',
        en: {
            title: 'Sman Cars Platform',
            category: 'Web Applications',
            tag: 'Automotive',
            summary:
                'A full-scale automotive sales platform with a complete admin suite, automated payment recognition, instant invoice status updates, and AI-powered webshop imagery.',
            details: {
                ...defaultDetails,
                en: {
                    breadcrumbLabel: 'Projects',
                    breadcrumbCategory: 'Web Application',
                    client: 'Sman Cars',
                    industry: 'Automotive',
                    duration: 'Ongoing',
                    service: 'Platform Development',
                    challengeTitle: 'The challenge',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Sman Cars needed a reliable platform to manage their full sales flow (inventory, customers, invoicing, and operational processes) without relying on disconnected tools.',
                        'A key pain point was payment follow-up: invoices often required manual checks and manual status changes, which increased overhead and slowed down operations.',
                        'They also wanted a more premium webshop presentation, with consistently strong vehicle imagery to improve trust and conversion.',
                    ],
                    solutionTitle: 'The solution',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We built a complete web platform with a full-featured admin environment to manage cars, sales, customers, and invoicing end-to-end.',
                        'The platform automatically recognizes incoming payments and updates invoice payment status to “paid”, reducing manual admin work and improving cashflow visibility.',
                        'We integrated AI capabilities to generate and enhance webshop-ready vehicle images, creating a consistent, high-quality presentation across listings.',
                    ],
                    technologiesTitle: 'Used technologies',
                    technologies: [
                        { icon: 'dataset', label: 'Laravel' },
                        { icon: 'code', label: 'Vue.js' },
                        { icon: 'database', label: 'MySQL' },
                        { icon: 'receipt_long', label: 'Invoicing' },
                        { icon: 'paid', label: 'Payment Detection' },
                        { icon: 'auto_awesome', label: 'AI Image Generation' },
                        { icon: 'cloud', label: 'Deployment & Hosting' },
                    ],
                    resultsTitle: 'The results',
                    resultsSubtitle:
                        'Less manual administration, faster payment processing, and a stronger webshop presentation.',
                    stats: [
                        {
                            value: 'Automated',
                            label: 'Payments',
                            description: 'Incoming payments are recognized and invoices are set to “paid” automatically.',
                        },
                        {
                            value: 'Full-suite',
                            label: 'Admin',
                            description: 'Centralized operations across inventory, sales, and invoicing.',
                        },
                        {
                            value: 'Consistent',
                            label: 'Webshop visuals',
                            description: 'AI-powered imagery improves quality and brand consistency across listings.',
                        },
                    ],
                    ctaTitle: 'Looking for a platform that reduces manual work?',
                    ctaSubtitle:
                        'We build scalable web platforms with automation and clear workflows, no fine print, just results.',
                },

                nl: {
                    // Optional: keep as placeholder fallback if you prefer to only maintain NL content in nl-block below.
                    ...defaultDetails.nl,
                },
            },
        },

        nl: {
            title: 'Sman Cars Platform',
            category: 'Web Applications',
            tag: 'Automotive',
            summary:
                'Een compleet automotive verkoopplatform met een volledige adminomgeving, automatische betalingsherkenning, facturen die direct op betaald worden gezet en AI voor webshopwaardige voertuigenfoto’s.',
            details: {
                ...defaultDetails,
                nl: {
                    breadcrumbLabel: 'Werk',
                    breadcrumbCategory: 'Webapplicatie',
                    client: 'Sman Cars',
                    industry: 'Automotive',
                    duration: 'Doorlopend',
                    service: 'Platformontwikkeling',
                    challengeTitle: 'De uitdaging',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Sman Cars had behoefte aan één betrouwbaar platform om het volledige verkoopproces te beheren: voorraad, klanten, facturatie en operationele workflows.',
                        'Een belangrijk knelpunt was de opvolging van betalingen: facturen moesten vaak handmatig gecontroleerd en handmatig op betaald gezet worden.',
                        'Daarnaast wilde Sman Cars een professionelere webshoppresentatie met consistent sterke voertuigbeelden voor meer vertrouwen en conversie.',
                    ],
                    solutionTitle: 'De oplossing',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We bouwden een compleet webplatform met een uitgebreide adminomgeving waarmee auto’s, verkopen, klanten en facturatie end-to-end beheerd worden.',
                        'Het systeem herkent automatisch binnenkomende betalingen en zet de factuurstatus direct op “betaald”, waardoor handmatig werk afneemt en de cashflow inzichtelijker wordt.',
                        'We integreerden AI-functionaliteit voor het genereren en verbeteren van webshopwaardige voertuigfoto’s, zodat listings een consistente en hoogwaardige uitstraling hebben.',
                    ],
                    technologiesTitle: 'Gebruikte technologieën',
                    technologies: [
                        { icon: 'dataset', label: 'Laravel' },
                        { icon: 'code', label: 'Vue.js' },
                        { icon: 'database', label: 'MySQL' },
                        { icon: 'receipt_long', label: 'Facturatie' },
                        { icon: 'paid', label: 'Betalingsherkenning' },
                        { icon: 'auto_awesome', label: 'AI beeldgeneratie' },
                    ],
                    resultsTitle: 'Het resultaat',
                    resultsSubtitle:
                        'Minder handmatige administratie, snellere betalingsverwerking en een sterkere webshoppresentatie.',
                    stats: [
                        {
                            value: 'Geautomatiseerd',
                            label: 'Betalingen',
                            description: 'Betalingen worden herkend en facturen automatisch op “betaald” gezet.',
                        },
                        {
                            value: 'Volledig',
                            label: 'Admin',
                            description: 'Centrale aansturing van voorraad, sales en facturatie.',
                        },
                        {
                            value: 'Consistent',
                            label: 'Webshop visuals',
                            description: 'AI-gestuurde beelden zorgen voor kwaliteit en consistentie in listings.',
                        },
                    ],
                    ctaTitle: 'Ook een platform dat handmatig werk vermindert?',
                    ctaSubtitle:
                        'Wij bouwen schaalbare webplatformen met automatisering en heldere workflows, geen kleine lettertjes, wel resultaat.',
                },

                en: {
                    // Optional fallback placeholder
                    ...defaultDetails.en,
                },
            },
        },
    },
    {
        slug: 'nextlegal-pwa',
        image: '/images/projects/nextlegal.webp',
        en: {
            title: 'NextLegal PWA Web App',
            category: 'Web Applications',
            tag: 'PWA',
            summary:
                'A modern Progressive Web App that connects to the NextLegal API to manage dossiers, clients, time tracking, and document access in one fast, mobile-friendly interface.',
            details: {
                ...defaultDetails,
                en: {
                    breadcrumbLabel: 'Projects',
                    breadcrumbCategory: 'PWA Web App',
                    client: 'NextLegal',
                    industry: 'Legal Tech',
                    duration: 'Ongoing',
                    service: 'PWA Development (API-driven)',
                    challengeTitle: 'The challenge',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'NextLegal required a modern web application that feels app-like, works reliably on desktop and mobile, and supports quick daily workflows.',
                        'Core functionality had to include viewing dossiers, managing client information, writing time entries, and accessing documents.',
                        'All data needed to be fully driven by the NextLegal API, ensuring one source of truth and consistent behavior across systems.',
                    ],
                    solutionTitle: 'The solution',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We built a new Progressive Web App (PWA) focused on speed, usability, and a clean workflow-first interface.',
                        'The app connects directly to the NextLegal API to retrieve and update dossiers, clients, time entries, and documents.',
                        'By using an API-first architecture, the PWA remains scalable and maintainable, while enabling future modules and integrations without rework.',
                    ],
                    technologiesTitle: 'Used technologies',
                    technologies: [
                        { icon: 'code', label: 'Vue.js' },
                        { icon: 'api', label: 'NextLegal API' },
                        { icon: 'web', label: 'PWA' },
                        { icon: 'lock', label: 'Auth & Security' }
                    ],
                    resultsTitle: 'The results',
                    resultsSubtitle:
                        'A fast, app-like web experience that centralizes daily work and stays fully aligned with the NextLegal API.',
                    stats: [
                        {
                            value: 'One app',
                            label: 'Daily workflows',
                            description: 'Dossiers, clients, time tracking, and documents available in one interface.',
                        },
                        {
                            value: 'API-first',
                            label: 'Architecture',
                            description: 'All features are driven by the NextLegal API for consistency and scalability.',
                        },
                        {
                            value: 'PWA',
                            label: 'Experience',
                            description: 'Mobile-friendly, app-like UX with modern performance and responsiveness.',
                        },
                    ],
                    ctaTitle: 'Need a modern PWA connected to your API?',
                    ctaSubtitle:
                        'We build fast, scalable frontends that turn complex systems into clean, usable products.',
                },

                nl: {
                    ...defaultDetails.nl,
                },
            },
        },

        nl: {
            title: 'NextLegal PWA Webapp',
            category: 'Web Applications',
            tag: 'PWA',
            summary:
                'Een moderne Progressive Web App die koppelt met de NextLegal API voor het inzien van dossiers, cliënten, uren schrijven en documenten bekijken in één snelle, mobielvriendelijke interface.',
            details: {
                ...defaultDetails,
                nl: {
                    breadcrumbLabel: 'Werk',
                    breadcrumbCategory: 'PWA Webapp',
                    client: 'NextLegal',
                    industry: 'Legal Tech',
                    duration: 'Doorlopend',
                    service: 'PWA ontwikkeling (API-driven)',
                    challengeTitle: 'De uitdaging',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'NextLegal had behoefte aan een moderne webapplicatie met een app-achtige ervaring die betrouwbaar werkt op desktop én mobiel.',
                        'De kernfunctionaliteiten moesten bestaan uit het inzien van dossiers, beheren van cliënten, uren schrijven en het bekijken van documenten.',
                        'Alle data moest volledig via de NextLegal API lopen, zodat er één bron van waarheid is en gedrag overal consistent blijft.',
                    ],
                    solutionTitle: 'De oplossing',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We bouwden een nieuwe Progressive Web App (PWA) met focus op snelheid, gebruiksgemak en een workflow-first interface.',
                        'De applicatie koppelt direct met de NextLegal API om dossiers, cliënten, uren en documenten op te halen en te wijzigen.',
                        'Door API-first te bouwen blijft de PWA schaalbaar en onderhoudbaar en kunnen toekomstige modules en integraties eenvoudig worden toegevoegd.',
                    ],
                    technologiesTitle: 'Gebruikte technologieën',
                    technologies: [
                        { icon: 'code', label: 'Vue.js' },
                        { icon: 'api', label: 'NextLegal API' },
                        { icon: 'web', label: 'PWA' },
                        { icon: 'lock', label: 'Authenticatie & Security' },
                        { icon: 'cloud', label: 'Deployment & Hosting' },
                    ],
                    resultsTitle: 'Het resultaat',
                    resultsSubtitle:
                        'Een snelle, app-achtige webervaring die dagelijks werk centraliseert en volledig in sync blijft met de NextLegal API.',
                    stats: [
                        {
                            value: 'Één app',
                            label: 'Dagelijkse taken',
                            description: 'Dossiers, cliënten, uren en documenten in één interface.',
                        },
                        {
                            value: 'API-first',
                            label: 'Architectuur',
                            description: 'Alle functionaliteit is gekoppeld aan de NextLegal API voor consistentie en schaalbaarheid.',
                        },
                        {
                            value: 'PWA',
                            label: 'Ervaring',
                            description: 'Mobielvriendelijke, app-achtige UX met moderne performance.',
                        },
                    ],
                    ctaTitle: 'Ook een moderne PWA gekoppeld aan je API?',
                    ctaSubtitle:
                        'Wij bouwen snelle, schaalbare frontends die complexe systemen omzetten naar heldere gebruikerservaringen.',
                },

                en: {
                    ...defaultDetails.en,
                },
            },
        },
    },
    {
        slug: 'broednest-platform',
        image: '/images/projects/broednest.webp',
        en: {
            title: 'Broednest Platform',
            category: 'Web Applications',
            tag: 'Content Platform',
            summary:
                'Maintenance and modernization of an existing content platform, including website, CMS, and admin environment, upgraded to the latest stack versions with AI integrations.',
            result: '[PLACEHOLDER: result Broednest, e.g. "X% faster load time after the stack upgrade"]',
            details: {
                ...defaultDetails,
                en: {
                    breadcrumbLabel: 'Projects',
                    breadcrumbCategory: 'Web Application',
                    client: 'Broednest',
                    industry: 'Lifestyle & Content',
                    duration: 'Ongoing',
                    service: 'Platform Maintenance & Modernization',
                    challengeTitle: 'The challenge',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Broednest runs a content-driven platform built on a combination of Symfony, React, and Sanity CMS.',
                        'Over time, parts of the stack became outdated, making maintenance, performance, and further development increasingly complex.',
                        'In addition, Broednest wanted to introduce smart AI-driven features without disrupting existing workflows.',
                    ],
                    solutionTitle: 'The solution',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We took over the maintenance of the existing platform and upgraded the full stack to current, stable versions.',
                        'The website, Sanity CMS setup, and Backstage admin environment were aligned and modernized to ensure long-term maintainability.',
                        'We introduced AI integrations to enhance content workflows and automation, while keeping the platform familiar for editors and administrators.',
                    ],
                    technologiesTitle: 'Used technologies',
                    technologies: [
                        { icon: 'dataset', label: 'Symfony' },
                        { icon: 'code', label: 'React' },
                        { icon: 'article', label: 'Sanity CMS' },
                        { icon: 'admin_panel_settings', label: 'Backstage' },
                        { icon: 'smart_toy', label: 'AI Integrations' },
                    ],
                    resultsTitle: 'The results',
                    resultsSubtitle:
                        'A future-proof content platform with a modernized stack and AI-enhanced workflows.',
                    stats: [
                        {
                            value: 'Up-to-date',
                            label: 'Stack',
                            description: 'All core technologies upgraded to supported, modern versions.',
                        },
                        {
                            value: 'Unified',
                            label: 'Platform',
                            description: 'Website, CMS, and admin environment aligned and maintained together.',
                        },
                        {
                            value: 'AI-powered',
                            label: 'Workflows',
                            description: 'Smart AI integrations improve efficiency and content handling.',
                        },
                    ],
                    ctaTitle: 'Maintaining a complex existing platform?',
                    ctaSubtitle:
                        'We modernize and extend existing systems without breaking what already works.',
                },

                nl: {
                    ...defaultDetails.nl,
                },
            },
        },

        nl: {
            title: 'Broednest Platform',
            category: 'Web Applications',
            tag: 'Contentplatform',
            summary:
                'Onderhoud en modernisering van een bestaand contentplatform, inclusief website, CMS en adminomgeving, volledig geüpdatet naar de nieuwste stack met AI-integraties.',
            result: '[PLACEHOLDER: resultaat Broednest, bijv. "X% snellere laadtijd na de stack-upgrade"]',
            details: {
                ...defaultDetails,
                nl: {
                    breadcrumbLabel: 'Werk',
                    breadcrumbCategory: 'Webapplicatie',
                    client: 'Broednest',
                    industry: 'Lifestyle & Content',
                    duration: 'Doorlopend',
                    service: 'Platformonderhoud & modernisering',
                    challengeTitle: 'De uitdaging',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Broednest maakt gebruik van een contentplatform gebouwd met Symfony, React en Sanity CMS.',
                        'Door verouderde onderdelen binnen de stack werd onderhoud lastiger en verdere ontwikkeling complexer.',
                        'Daarnaast was er de wens om slimme AI-functionaliteit toe te voegen zonder bestaande workflows te verstoren.',
                    ],
                    solutionTitle: 'De oplossing',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'Wij namen het onderhoud van het bestaande platform over en brachten de volledige stack naar stabiele, actuele versies.',
                        'De website, Sanity CMS-configuratie en Backstage adminomgeving zijn opnieuw op elkaar afgestemd en gemoderniseerd.',
                        'We voegden AI-integraties toe om contentprocessen slimmer en efficiënter te maken, terwijl de vertrouwde werkwijze behouden bleef.',
                    ],
                    technologiesTitle: 'Gebruikte technologieën',
                    technologies: [
                        { icon: 'dataset', label: 'Symfony' },
                        { icon: 'code', label: 'React' },
                        { icon: 'article', label: 'Sanity CMS' },
                        { icon: 'admin_panel_settings', label: 'Backstage' },
                        { icon: 'smart_toy', label: 'AI-integraties' },
                    ],
                    resultsTitle: 'Het resultaat',
                    resultsSubtitle:
                        'Een toekomstbestendig contentplatform met een gemoderniseerde stack en AI-ondersteunde workflows.',
                    stats: [
                        {
                            value: 'Actueel',
                            label: 'Stack',
                            description: 'Alle kerntechnologieën geüpdatet naar moderne, ondersteunde versies.',
                        },
                        {
                            value: 'Samenhang',
                            label: 'Platform',
                            description: 'Website, CMS en adminomgeving worden centraal onderhouden.',
                        },
                        {
                            value: 'Slim',
                            label: 'AI',
                            description: 'AI-integraties verbeteren efficiëntie en contentafhandeling.',
                        },
                    ],
                    ctaTitle: 'Een bestaand platform moderniseren?',
                    ctaSubtitle:
                        'Wij houden complexe systemen gezond en klaar voor de toekomst, zonder alles opnieuw te hoeven bouwen.',
                },

                en: {
                    ...defaultDetails.en,
                },
            },
        },
    },
    {
        slug: 'tribeo-marketplace',
        image: '/images/projects/tribeo.webp',
        en: {
            title: 'Tribeo Marketplace Platform',
            category: 'Web Applications',
            tag: 'Marketplace',
            summary:
                'A multi-marketplace platform that allows suppliers to create and manage marketplaces, add products, and operate within dedicated management environments.',
            details: {
                ...defaultDetails,
                en: {
                    breadcrumbLabel: 'Projects',
                    breadcrumbCategory: 'Web Application',
                    client: 'Tribeo',
                    industry: 'B2B Marketplace',
                    duration: 'Ongoing',
                    service: 'Marketplace Platform Development',
                    challengeTitle: 'The challenge',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Tribeo wanted to build a flexible platform where multiple marketplaces could be created and managed independently.',
                        'Suppliers needed a clear and structured environment to add and manage their own products, without affecting other marketplaces.',
                        'The platform had to scale as new suppliers and marketplaces were added, while keeping management intuitive and secure.',
                    ],
                    solutionTitle: 'The solution',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We built a custom marketplace platform where marketplaces can be created dynamically and configured per use case.',
                        'Suppliers are provided with dedicated management environments to add, update, and manage their products.',
                        'The system is built with a clear separation of roles and permissions, ensuring a scalable and maintainable architecture.',
                    ],
                    technologiesTitle: 'Used technologies',
                    technologies: [
                        { icon: 'dataset', label: 'Laravel' },
                        { icon: 'code', label: 'Vue.js' },
                        { icon: 'database', label: 'MySQL' },
                        { icon: 'storefront', label: 'Marketplace Logic' },
                        { icon: 'admin_panel_settings', label: 'Role-based Admin' },
                    ],
                    resultsTitle: 'The results',
                    resultsSubtitle:
                        'A scalable marketplace platform with clear supplier workflows and independent management environments.',
                    stats: [
                        {
                            value: 'Multi',
                            label: 'Marketplaces',
                            description: 'Supports the creation and management of multiple independent marketplaces.',
                        },
                        {
                            value: 'Supplier-first',
                            label: 'Management',
                            description: 'Dedicated environments for suppliers to manage their own products.',
                        },
                        {
                            value: 'Scalable',
                            label: 'Architecture',
                            description: 'Built to grow with additional suppliers and marketplaces.',
                        },
                    ],
                    ctaTitle: 'Building a marketplace platform?',
                    ctaSubtitle:
                        'We develop scalable marketplace solutions with clear roles, clean workflows, and long-term maintainability.',
                },

                nl: {
                    ...defaultDetails.nl,
                },
            },
        },

        nl: {
            title: 'Tribeo Marketplace Platform',
            category: 'Web Applications',
            tag: 'Marktplaats',
            summary:
                'Een marketplace-platform waarmee meerdere marktplaatsen kunnen worden aangemaakt en leveranciers via eigen beheeromgevingen producten kunnen toevoegen en beheren.',
            details: {
                ...defaultDetails,
                nl: {
                    breadcrumbLabel: 'Werk',
                    breadcrumbCategory: 'Webapplicatie',
                    client: 'Tribeo',
                    industry: 'B2B Marktplaats',
                    duration: 'Doorlopend',
                    service: 'Marketplace platformontwikkeling',
                    challengeTitle: 'De uitdaging',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Tribeo had behoefte aan een flexibel platform waarin meerdere marktplaatsen onafhankelijk van elkaar kunnen worden opgezet.',
                        'Leveranciers moesten beschikken over een duidelijke beheeromgeving om hun eigen producten toe te voegen en te beheren.',
                        'Het platform moest schaalbaar zijn en overzichtelijk blijven naarmate het aantal leveranciers en marktplaatsen groeit.',
                    ],
                    solutionTitle: 'De oplossing',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We ontwikkelden een maatwerk marketplace-platform waarin marktplaatsen dynamisch kunnen worden aangemaakt en ingericht.',
                        'Leveranciers krijgen een eigen beheeromgeving om producten toe te voegen, aan te passen en te beheren.',
                        'Door een duidelijke rol- en rechtenstructuur is het platform schaalbaar, veilig en goed onderhoudbaar.',
                    ],
                    technologiesTitle: 'Gebruikte technologieën',
                    technologies: [
                        { icon: 'dataset', label: 'Laravel' },
                        { icon: 'code', label: 'Vue.js' },
                        { icon: 'database', label: 'MySQL' },
                        { icon: 'storefront', label: 'Marketplace logica' },
                        { icon: 'admin_panel_settings', label: 'Rolgebaseerde admin' },
                    ],
                    resultsTitle: 'Het resultaat',
                    resultsSubtitle:
                        'Een schaalbaar marketplace-platform met duidelijke workflows voor leveranciers en beheerders.',
                    stats: [
                        {
                            value: 'Meerdere',
                            label: 'Marktplaatsen',
                            description: 'Onafhankelijke marktplaatsen binnen één platform.',
                        },
                        {
                            value: 'Leverancier',
                            label: 'Beheer',
                            description: 'Eigen omgevingen voor productbeheer per leverancier.',
                        },
                        {
                            value: 'Schaalbaar',
                            label: 'Architectuur',
                            description: 'Ontworpen om mee te groeien met nieuwe marktplaatsen en aanbieders.',
                        },
                    ],
                    ctaTitle: 'Ook een marketplace bouwen?',
                    ctaSubtitle:
                        'Wij bouwen schaalbare marktplaatsplatformen met heldere rollen, slimme workflows en toekomstbestendige techniek.',
                },

                en: {
                    ...defaultDetails.en,
                },
            },
        },
    },
    {
        slug: 'highmark-software-website',
        image: '/images/projects/highmark.webp',
        en: {
            title: 'Highmark Software Website',
            category: 'Websites',
            tag: 'Corporate Website',
            summary:
                'A fast and professional company website built in Framer, providing Highmark Software with a clean, modern online presence.',
            result: '[PLACEHOLDER: result Highmark, e.g. "Live in X weeks" or "X% more enquiries"]',
            details: {
                ...defaultDetails,
                en: {
                    breadcrumbLabel: 'Projects',
                    breadcrumbCategory: 'Website',
                    client: 'Highmark Software',
                    industry: 'Software & Technology',
                    duration: 'Short delivery',
                    service: 'Website Design & Build (Framer)',
                    challengeTitle: 'The challenge',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Highmark Software needed a professional online presence that could be delivered quickly.',
                        'The website had to clearly communicate their services and expertise, without the overhead of a custom-built platform.',
                    ],
                    solutionTitle: 'The solution',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We designed and built a modern company website using Framer.',
                        'This allowed for rapid delivery, clean animations, and a polished look and feel without compromising on quality.',
                        'The result is a website that is easy to maintain and flexible enough to grow with the company.',
                    ],
                    technologiesTitle: 'Used technologies',
                    technologies: [
                        { icon: 'design_services', label: 'Framer' },
                        { icon: 'palette', label: 'UI Design' },
                        { icon: 'speed', label: 'Fast Delivery' },
                    ],
                    resultsTitle: 'The results',
                    resultsSubtitle:
                        'A clean and professional website that positions Highmark Software as a credible technology partner.',
                    stats: [
                        {
                            value: 'Fast',
                            label: 'Delivery',
                            description: 'Website delivered quickly using Framer.',
                        },
                        {
                            value: 'Modern',
                            label: 'Design',
                            description: 'Clean layout with subtle animations.',
                        },
                        {
                            value: 'Easy',
                            label: 'Maintenance',
                            description: 'Simple content updates without developer dependency.',
                        },
                    ],
                    ctaTitle: 'Need a professional website quickly?',
                    ctaSubtitle:
                        'We deliver clean, modern websites using Framer or custom builds, depending on what fits your goals.',
                },

                nl: {
                    ...defaultDetails.nl,
                },
            },
        },

        nl: {
            title: 'Highmark Software Website',
            category: 'Websites',
            tag: 'Bedrijfswebsite',
            summary:
                'Een snelle en professionele bedrijfswebsite gebouwd in Framer, waarmee Highmark Software beschikt over een moderne online uitstraling.',
            result: '[PLACEHOLDER: resultaat Highmark, bijv. "Live in X weken" of "X% meer aanvragen"]',
            details: {
                ...defaultDetails,
                nl: {
                    breadcrumbLabel: 'Werk',
                    breadcrumbCategory: 'Website',
                    client: 'Highmark Software',
                    industry: 'Software & Technologie',
                    duration: 'Korte doorlooptijd',
                    service: 'Website ontwerp & realisatie (Framer)',
                    challengeTitle: 'De uitdaging',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Highmark Software had behoefte aan een professionele online aanwezigheid die snel gerealiseerd kon worden.',
                        'De website moest hun diensten en expertise helder communiceren, zonder de complexiteit van een maatwerkplatform.',
                    ],
                    solutionTitle: 'De oplossing',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We ontwierpen en bouwden een moderne bedrijfswebsite met Framer.',
                        'Dit maakte snelle oplevering mogelijk, met een strakke vormgeving en subtiele animaties.',
                        'De website is eenvoudig te onderhouden en kan makkelijk meegroeien met het bedrijf.',
                    ],
                    technologiesTitle: 'Gebruikte technologieën',
                    technologies: [
                        { icon: 'design_services', label: 'Framer' },
                        { icon: 'palette', label: 'UI Design' },
                        { icon: 'speed', label: 'Snelle oplevering' },
                    ],
                    resultsTitle: 'Het resultaat',
                    resultsSubtitle:
                        'Een professionele website die Highmark Software neerzet als betrouwbare technologiepartner.',
                    stats: [
                        {
                            value: 'Snel',
                            label: 'Opgeleverd',
                            description: 'Website snel gerealiseerd met Framer.',
                        },
                        {
                            value: 'Modern',
                            label: 'Design',
                            description: 'Strakke uitstraling met lichte animaties.',
                        },
                        {
                            value: 'Eenvoudig',
                            label: 'Beheer',
                            description: 'Content eenvoudig aanpasbaar zonder developers.',
                        },
                    ],
                    ctaTitle: 'Ook snel een professionele website nodig?',
                    ctaSubtitle:
                        'Wij bouwen moderne websites in Framer of maatwerk, altijd passend bij je doelstellingen.',
                },

                en: {
                    ...defaultDetails.en,
                },
            },
        },
    },
    {
        slug: 'suporta-platform',
        image: '/images/projects/suporta.webp',
        en: {
            title: 'Suporta Platform',
            category: 'Web Applications',
            tag: 'Internal Tooling',
            summary:
                'An internal platform built by BitPeak to streamline support, project management, and time tracking into one unified system.',
            details: {
                ...defaultDetails,
                en: {
                    breadcrumbLabel: 'Projects',
                    breadcrumbCategory: 'Internal Tool',
                    client: 'BitPeak (Internal)',
                    industry: 'Productivity & Operations',
                    duration: 'Ongoing',
                    service: 'Product Development',
                    challengeTitle: 'The challenge',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'As BitPeak grew, support requests, project tracking, and time registration were spread across multiple tools.',
                        'This fragmentation caused overhead, context switching, and inconsistent workflows across teams and projects.',
                        'There was a need for a single internal tool tailored to BitPeak’s way of working.',
                    ],
                    solutionTitle: 'The solution',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We built Suporta as an internal platform to centralize support tickets, project management, and time tracking.',
                        'The system is designed around clear workflows, making it easy for team members to handle support, track progress, and log hours.',
                        'By building the tool ourselves, we can continuously adapt it to real-world needs and internal processes.',
                    ],
                    technologiesTitle: 'Used technologies',
                    technologies: [
                        { icon: 'dataset', label: 'Laravel' },
                        { icon: 'code', label: 'Vue.js' },
                        { icon: 'support_agent', label: 'Support System' },
                        { icon: 'task_alt', label: 'Project Management' },
                        { icon: 'schedule', label: 'Time Tracking' },
                    ],
                    resultsTitle: 'The results',
                    resultsSubtitle:
                        'A single internal platform that reduces overhead and keeps teams aligned.',
                    stats: [
                        {
                            value: 'One tool',
                            label: 'Workflow',
                            description: 'Support, projects, and hours managed in one place.',
                        },
                        {
                            value: 'Tailored',
                            label: 'Processes',
                            description: 'Built specifically for BitPeak’s internal way of working.',
                        },
                        {
                            value: 'Scalable',
                            label: 'Foundation',
                            description: 'Continuously extended as new needs arise.',
                        },
                    ],
                    ctaTitle: 'Need better internal tooling?',
                    ctaSubtitle:
                        'We build practical internal tools that streamline workflows and scale with your team.',
                },

                nl: {
                    ...defaultDetails.nl,
                },
            },
        },

        nl: {
            title: 'Suporta Platform',
            category: 'Web Applications',
            tag: 'Interne tooling',
            summary:
                'Een door BitPeak zelf ontwikkeld platform om support, projectmanagement en urenregistratie te stroomlijnen in één systeem.',
            details: {
                ...defaultDetails,
                nl: {
                    breadcrumbLabel: 'Werk',
                    breadcrumbCategory: 'Interne tool',
                    client: 'BitPeak (intern)',
                    industry: 'Productiviteit & Operations',
                    duration: 'Doorlopend',
                    service: 'Productontwikkeling',
                    challengeTitle: 'De uitdaging',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Met de groei van BitPeak werden supportvragen, projectbeheer en urenregistratie verdeeld over meerdere tools.',
                        'Dit zorgde voor extra overhead, contextwisselingen en inconsistente werkwijzen binnen het team.',
                        'Er was behoefte aan één interne tool die volledig aansluit op de manier van werken binnen BitPeak.',
                    ],
                    solutionTitle: 'De oplossing',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We ontwikkelden Suporta als intern platform waarin supporttickets, projectmanagement en urenregistratie samenkomen.',
                        'Het systeem is opgebouwd rondom duidelijke workflows, zodat teamleden efficiënt support kunnen afhandelen, projecten volgen en uren registreren.',
                        'Doordat BitPeak het platform zelf bouwt, kan het continu worden aangepast aan de praktijk en interne behoeften.',
                    ],
                    technologiesTitle: 'Gebruikte technologieën',
                    technologies: [
                        { icon: 'dataset', label: 'Laravel' },
                        { icon: 'code', label: 'Vue.js' },
                        { icon: 'support_agent', label: 'Support systeem' },
                        { icon: 'task_alt', label: 'Projectmanagement' },
                        { icon: 'schedule', label: 'Urenregistratie' },
                    ],
                    resultsTitle: 'Het resultaat',
                    resultsSubtitle:
                        'Eén intern platform dat overhead vermindert en teams beter op elkaar afstemt.',
                    stats: [
                        {
                            value: 'Eén tool',
                            label: 'Workflow',
                            description: 'Support, projecten en uren in één omgeving.',
                        },
                        {
                            value: 'Op maat',
                            label: 'Processen',
                            description: 'Volledig afgestemd op de werkwijze van BitPeak.',
                        },
                        {
                            value: 'Schaalbaar',
                            label: 'Fundament',
                            description: 'Doorlopend uitbreidbaar met nieuwe functionaliteit.',
                        },
                    ],
                    ctaTitle: 'Ook behoefte aan betere interne tooling?',
                    ctaSubtitle:
                        'Wij bouwen praktische tools die interne processen vereenvoudigen en meegroeien met je organisatie.',
                },

                en: {
                    ...defaultDetails.en,
                },
            },
        },
    },
    {
        slug: 'songup-platform',
        image: '/images/projects/songup.webp',
        en: {
            title: 'SongUp',
            category: 'Web Applications',
            tag: 'Fun Project',
            summary:
                'An experimental platform by BitPeak that allows party guests to request songs via QR codes, while DJs manage events and requests in real time.',
            details: {
                ...defaultDetails,
                en: {
                    breadcrumbLabel: 'Projects',
                    breadcrumbCategory: 'Fun Project',
                    client: 'BitPeak (Internal)',
                    industry: 'Events & Entertainment',
                    duration: 'Ongoing',
                    service: 'Product Experiment & Platform Development',
                    challengeTitle: 'The challenge',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'At events and parties, song requests are often chaotic and difficult for DJs to manage.',
                        'BitPeak wanted to explore a playful but technically solid solution that improves interaction between DJs and their audience.',
                        'The platform needed to be simple for guests while giving DJs real-time control.',
                    ],
                    solutionTitle: 'The solution',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We built SongUp as a fun internal project where DJs can create events and generate QR codes.',
                        'Guests scan the QR code to request songs directly from their phone, without needing an account.',
                        'DJs manage incoming requests in real time, mark songs as played, and keep full control over the event flow.',
                    ],
                    technologiesTitle: 'Used technologies',
                    technologies: [
                        { icon: 'dataset', label: 'Laravel' },
                        { icon: 'code', label: 'Vue.js' },
                        { icon: 'qr_code', label: 'QR Codes' },
                        { icon: 'music_note', label: 'Spotify API' },
                        { icon: 'bolt', label: 'Real-time Updates' },
                    ],
                    resultsTitle: 'The results',
                    resultsSubtitle:
                        'A playful yet robust platform that showcases real-time interaction and rapid product development.',
                    stats: [
                        {
                            value: 'QR-based',
                            label: 'Requests',
                            description: 'Guests request songs instantly via QR codes.',
                        },
                        {
                            value: 'Real-time',
                            label: 'Control',
                            description: 'DJs manage and process requests live during events.',
                        },
                        {
                            value: 'Experimental',
                            label: 'Product',
                            description: 'A fun project used to explore ideas, UX, and technical concepts.',
                        },
                    ],
                    ctaTitle: 'Looking to build something fun and functional?',
                    ctaSubtitle:
                        'We enjoy experimenting with new ideas and turning them into working products.',
                },

                nl: {
                    ...defaultDetails.nl,
                },
            },
        },

        nl: {
            title: 'SongUp',
            category: 'Web Applications',
            tag: 'Fun project',
            summary:
                'Een experimenteel platform van BitPeak waarmee feestgangers via QR-codes nummers kunnen aanvragen en DJ’s events en verzoeken realtime beheren.',
            details: {
                ...defaultDetails,
                nl: {
                    breadcrumbLabel: 'Werk',
                    breadcrumbCategory: 'Fun project',
                    client: 'BitPeak (intern)',
                    industry: 'Events & Entertainment',
                    duration: 'Doorlopend',
                    service: 'Productexperiment & platformontwikkeling',
                    challengeTitle: 'De uitdaging',
                    challengeIcon: 'warning',
                    challengeParagraphs: [
                        'Tijdens events en feesten verlopen muziekaanvragen vaak chaotisch en onoverzichtelijk voor DJ’s.',
                        'BitPeak wilde een speels maar technisch solide concept uitwerken dat de interactie tussen DJ en publiek verbetert.',
                        'De oplossing moest laagdrempelig zijn voor gasten en realtime controle bieden aan DJ’s.',
                    ],
                    solutionTitle: 'De oplossing',
                    solutionIcon: 'lightbulb',
                    solutionParagraphs: [
                        'We ontwikkelden SongUp als fun project waarin DJ’s events aanmaken en QR-codes genereren.',
                        'Bezoekers scannen de QR-code en kunnen direct via hun telefoon nummers aanvragen, zonder account.',
                        'DJ’s beheren alle aanvragen realtime, markeren nummers als gespeeld en houden controle over de avond.',
                    ],
                    technologiesTitle: 'Gebruikte technologieën',
                    technologies: [
                        { icon: 'dataset', label: 'Laravel' },
                        { icon: 'code', label: 'Vue.js' },
                        { icon: 'qr_code', label: 'QR-codes' },
                        { icon: 'music_note', label: 'Spotify API' },
                        { icon: 'bolt', label: 'Realtime updates' },
                    ],
                    resultsTitle: 'Het resultaat',
                    resultsSubtitle:
                        'Een speels maar robuust platform dat realtime interactie en snelle productontwikkeling laat zien.',
                    stats: [
                        {
                            value: 'QR-gedreven',
                            label: 'Aanvragen',
                            description: 'Feestgangers vragen nummers aan via QR-codes.',
                        },
                        {
                            value: 'Realtime',
                            label: 'Beheer',
                            description: 'DJ’s beheren aanvragen live tijdens events.',
                        },
                        {
                            value: 'Experimenteel',
                            label: 'Product',
                            description: 'Fun project om ideeën, UX en techniek te verkennen.',
                        },
                    ],
                    ctaTitle: 'Ook een speels idee omzetten naar een product?',
                    ctaSubtitle:
                        'We bouwen niet alleen serieuze platforms, maar experimenteren ook graag met nieuwe concepten.',
                },

                en: {
                    ...defaultDetails.en,
                },
            },
        },
    }










];
